import {Injectable} from '@angular/core';

import {Observable} from 'rxjs/Observable';
import {ConfigService} from '../../../core/services/config.service';
import {RequestService} from '../../../core/services/request.service';

import {EmployeeDetails} from './details/employee-details.model';
import {SessionService} from '../../../core/services/session.service';

@Injectable()
export class EmployeeService {
    constructor(private configService: ConfigService, private requestService: RequestService, private sessionService: SessionService) {
    }

    delete(details: any): Observable<EmployeeDetails> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').organization.employees.delete.api;
        return this.requestService.post(url, details);
    }

    add(details: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').organization.employees.add.api;
        return this.requestService.post(url, details);
    }

    addInstructor(details: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').organization.employees.addInstructor.api;
        return this.requestService.post(url, details);
    }

    edit(details: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.employees.edit.api;
        return this.requestService.post(url, details);
    }

    activate(details: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.employees.activate.api;
        return this.requestService.post(url, details);
    }

    deactivate(details: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.employees.deactivate.api;
        return this.requestService.post(url, details);
    }

    invite(details: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.employees.invite.api;
        return this.requestService.post(url, details);
    }

    getFields() {
        let fields = [
            {
                key: 'id',
                label: 'ID',
                type: 'hidden',
                required: false
            },
            {
                key: 'email',
                label: 'Email',
                type: 'textbox',
                pattern: 'email',
                required: true
            },
            {
                key: 'firstName',
                label: 'First Name',
                type: 'textbox',
                required: true
            },
            {
                key: 'lastName',
                label: 'Last Name',
                type: 'textbox',
                required: true
            },
            {
                key: 'address.id',
                label: 'Address',
                type: 'hidden'
            },
            {
                key: 'department.id',
                label: 'Department',
                type: 'dropdown',
                required: false,
                dictionaryUrl:
                    this.configService.get('server').url +
                    this.configService.get('endpoints').agora.dictionary.department.api
            },
            {
                key: 'roles',
                label: 'Roles',
                type: 'multi-dropdown',
                required: true,
                dictionaryUrl:
                    this.configService.get('server').url +
                    this.configService.get('endpoints').organization.employees.roles.api
            }
        ];
        return fields;
    }

    getInstructorFields() {
        let fields = [
            {
                key: 'id',
                label: 'ID',
                type: 'hidden',
                required: false
            },
            {
                key: 'email',
                label: 'Email',
                type: 'textbox',
                pattern: 'email',
                required: true
            },
            {
                key: 'firstName',
                label: 'First Name',
                type: 'textbox',
                required: true
            },
            {
                key: 'lastName',
                label: 'Last Name',
                type: 'textbox',
                required: true
            },
            {
                key: 'address.id',
                label: 'Address',
                type: 'hidden'
            }
        ];
        return fields;
    }

    getEmployeeDetails(id: string): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.employees.details.api +
            '/' +
            id;
        return this.requestService.get(url);
    }

    getDetailsFields() {
        let fields = [
            {
                key: 'firstName',
                label: 'First Name',
                type: 'text'
            },
            {
                key: 'lastName',
                label: 'Last Name',
                type: 'text'
            },
            {
                key: 'email',
                label: 'Email',
                type: 'text'
            },
            {
                key: (data: any) => {
                    if (data != null && data['address'] != null) {
                        let address = data['address'];
                        return this.getFormattedAddress(address);
                    } else {
                        return [''];
                    }
                },
                label: 'Address',
                type: 'text'
            },
            {
                key: 'department.configValue',
                label: 'Department',
                type: 'text'
            },
            {
                key: 'roles',
                label: 'Roles',
                getter: item => {
                    return item.configValue;
                },
                type: 'list'
            },
            {
                key: 'active',
                label: 'Is Active',
                type: 'boolean'
            }
        ];
        return fields;
    }

    getEditFields() {
        let fields = [
            {
                key: 'id',
                label: 'ID',
                type: 'hidden',
                required: false
            },
            {
                key: 'employeeId',
                label: 'Employee ID',
                type: 'hidden',
                required: true
            },
            {
                key: 'email',
                label: 'Email',
                type: 'textbox',
                pattern: 'email',
                required: true
            },
            {
                key: 'firstName',
                label: 'First Name',
                type: 'textbox',
                required: true
            },
            {
                key: 'lastName',
                label: 'Last Name',
                type: 'textbox',
                required: true
            },
            {
                key: 'address.id',
                label: 'Address',
                type: 'hidden',
                required: false,
            },
            {
                key: 'department.id',
                label: 'Department',
                type: 'dropdown',
                required: false,
                dictionaryUrl:
                    this.configService.get('server').url +
                    this.configService.get('endpoints').agora.dictionary.department.api
            },
            {
                key: 'roles',
                label: 'Roles',
                type: 'multi-dropdown',
                dictionaryUrl:
                    this.configService.get('server').url +
                    this.configService.get('endpoints').organization.employees.roles.api
            },
            {
                key: 'active',
                label: 'Active',
                type: 'radio',
                required: false,
                options: [{key: true, value: 'Yes'}, {key: false, value: 'No'}],
                default: false
            }
        ];
        return fields;
    }

    getFormattedAddress(address: any): string {
        return (
            address.country +
            ' ' +
            address.state +
            ' ' +
            address.province +
            ' ' +
            address.city +
            ' ' +
            address.streetName +
            ' ' +
            address.zipCode
        )
            .replace(/null/g, '')
            .replace(/undefined/g, '')
            .replace(/  /g, '');
    }

    getAccountByDomain(): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.accounts.domain.api;
        return this.requestService.get(url);
    }

    promoteToInstructor(id: string): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.employees.promoteToInstructor.api + `/${id}`;
        return this.requestService.post(url, {});
    }

    checkIfUserIsAdmin() {
        let details = this.sessionService.getUserDetails();
        let adminGroups = ['seller-admin', 'buyer-admin', 'administrator', 'itpx'];
        let groups = details && details['groups'] && details['groups'].length;
        return groups && details['groups'].find(_ => adminGroups.includes(_));
    }
}



// WEBPACK FOOTER //
// ./src/app/organization/components/employees/employee.service.ts